import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AlertCircle, CheckCircle, Clock, CreditCard } from "lucide-react";
import { BillingService, Bill, PayBillResponse } from "../services/billingService";
import { formatPrice } from "../utils/utility";
import useToastStore from "../store/useToastStore";

type BillStatus = Bill["status"];

const BillingPage = () => {
  const { showToast } = useToastStore();
  const queryClient = useQueryClient();
  const [payingBillId, setPayingBillId] = useState<string | null>(null);

  const { data: bills, isLoading, error, refetch } = useQuery({
    queryKey: ["bills"],
    queryFn: () => BillingService.fetchBills(),
  });

  const payBillMutation = useMutation({
    mutationFn: (billId: string) => BillingService.payBill(billId),
    onSuccess: (data: PayBillResponse, billId: string) => {
      setPayingBillId(null);
      // Redirect to payment gateway URL
      if (data.redirectUrl) {
        // Note: In a real implementation, the payment gateway should redirect back to:
        // /admin/billing/confirm/{billId} after payment processing
        window.location.href = data.redirectUrl;
      } else {
        // Fallback if no redirect URL is provided
        showToast("Payment initiated successfully!", "success");
      }
    },
    onError: (error: any, billId: string) => {
      setPayingBillId(null);
      const errorMessage = error.response?.data?.message || error.message || "Failed to initiate payment";
      showToast(errorMessage, "error");
    },
  });

  const handlePayBill = (billId: string) => {
    setPayingBillId(billId);
    payBillMutation.mutate(billId);
  };

  const getStatusBadge = (status: BillStatus) => {
    const statusConfig: Record<BillStatus, { bg: string; text: string; label: string }> = {
      PENDING: {
        bg: "bg-yellow-100 text-yellow-800",
        text: "Pending",
        label: "Pending"
      },
      PAID: {
        bg: "bg-green-100 text-green-800",
        text: "Paid",
        label: "Paid"
      },
      OVERDUE: {
        bg: "bg-red-100 text-red-800",
        text: "Overdue",
        label: "Overdue"
      }
    };

    const config = statusConfig[status];

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatBillingPeriod = (month: number, year: number) => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-2">Error loading bills</p>
          <p className="text-gray-500 text-sm mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
      </div>

      {bills && bills.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Generated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bills.map((bill: Bill) => (
                  <tr key={bill._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatBillingPeriod(bill.month, bill.year)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatPrice(bill.totalSales)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatPrice(bill.billAmount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(bill.generatedDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(bill.dueDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center space-y-1">
                        {getStatusBadge(bill.status)}
                        {bill.status === 'PAID' && bill.paidOn && (
                          <span className="text-xs text-gray-500">
                            Paid on {formatDate(bill.paidOn)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {(bill.status === 'PENDING' || bill.status === 'OVERDUE') && (
                        <button
                          onClick={() => handlePayBill(bill._id)}
                          disabled={payingBillId === bill._id}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {payingBillId === bill._id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent mr-1"></div>
                              Redirecting...
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-3 h-3 mr-1" />
                              Pay
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-gray-200">
            {bills.map((bill: Bill) => (
              <div key={bill._id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-medium text-gray-900">
                    {formatBillingPeriod(bill.month, bill.year)}
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    {getStatusBadge(bill.status)}
                    {bill.status === 'PAID' && bill.paidOn && (
                      <span className="text-xs text-gray-500">
                        Paid on {formatDate(bill.paidOn)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Sales:</span>
                    <span className="font-medium">{formatPrice(bill.totalSales)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">{formatPrice(bill.billAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Generated:</span>
                    <span>{formatDate(bill.generatedDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Due:</span>
                    <span>{formatDate(bill.dueDate)}</span>
                  </div>
                </div>

                {(bill.status === 'PENDING' || bill.status === 'OVERDUE') && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handlePayBill(bill._id)}
                      disabled={payingBillId === bill._id}
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {payingBillId === bill._id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent mr-2"></div>
                          Redirecting...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Pay Bill
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📄</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Bills Yet</h3>
          <p className="text-gray-500">Your billing statements will appear here once you start making sales.</p>
        </div>
      )}

      {/* Billing Calculation Explanation - Always visible */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm">💡</span>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm text-blue-800">
              Bills are calculated as <strong>2% commission</strong> on monthly sales.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
